import { PLATFORM_ROLES } from "@cafe/shared";
import { Badge, Button, DataTable, dialogs, EmptyState, Field, FormGrid, Input, Modal, NumberInput, PageHeader, PasswordInput, Select, toast } from "@cafe/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { dt, errorText, http, ROLES } from "../lib/api";

type U = { id: string; email: string; name: string; role: string; isActive: boolean; totpEnabled: boolean; lastLoginAt: string | null; commissionPercent: number };

/** Сотрудники платформы: роли, 2FA, отключение. */
export default function StaffPage() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["staff"], queryFn: () => http.get<U[]>("/staff") });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", name: "", role: "support", password: "" });
  const [edit, setEdit] = useState<{ id: string; name: string; role: string; commissionPercent: number | null } | null>(null);
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      void qc.invalidateQueries({ queryKey: ["staff"] });
      return true;
    } catch (e) {
      toast.error(errorText(e));
      return false;
    }
  };
  return (
    <>
      <PageHeader title="Сотрудники платформы" description="Входят по паролю и коду из приложения на телефоне. Все действия записываются в журнал." actions={<Button leftIcon={<Plus />} onClick={() => setOpen(true)}>Добавить</Button>} />
      <DataTable rows={list.data} loading={list.isPending} rowKey={(u) => u.id} empty={<EmptyState title="Нет" />}
        columns={[
          { key: "n", header: "Сотрудник", cell: (u) => <div><p className="font-medium">{u.name}</p><p className="text-xs text-muted">{u.email}</p></div>, mobile: "title" },
          { key: "r", header: "Роль", cell: (u) => ROLES[u.role] ?? u.role, mobile: "subtitle" },
          { key: "p", header: "Вознаграждение", align: "end", cell: (u) => (u.commissionPercent ? `${u.commissionPercent}% от оплат` : "—") },
          { key: "2", header: "Код с телефона", cell: (u) => <Badge tone={u.totpEnabled ? "success" : "warning"}>{u.totpEnabled ? "настроен" : "не настроен"}</Badge> },
          { key: "l", header: "Последний вход", cell: (u) => dt(u.lastLoginAt) },
          { key: "s", header: "", cell: (u) => (u.isActive ? null : <Badge tone="danger">отключён</Badge>) },
        ]}
        actions={(u) => u.isActive && (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEdit({ id: u.id, name: u.name, role: u.role, commissionPercent: u.commissionPercent })}>Изменить</Button>
            <Button size="sm" variant="ghost" onClick={() => void dialogs.confirm({ title: `Сбросить код входа для ${u.name}?`, description: "При следующем входе сотрудник заново подключит приложение для кодов на телефоне.", confirmText: "Сбросить", onConfirm: async () => void run(() => http.post(`/staff/${u.id}/reset-2fa`), "Код входа сброшен") })}>Сбросить код входа</Button>
            <Button size="sm" variant="ghost" onClick={() => void run(() => http.post("/auth/password/forgot", { email: u.email }), `Ссылка для нового пароля отправлена на ${u.email}`)}>Ссылка для сброса пароля</Button>
            <Button size="sm" variant="ghost" onClick={() => void dialogs.confirmDanger({ title: `Отключить ${u.name}?`, confirmText: "Отключить", onConfirm: async () => void run(() => http.post(`/staff/${u.id}/deactivate`), "Отключён") })}>Отключить</Button>
          </div>
        )} />
      {edit && (
        <Modal open onOpenChange={(o) => !o && setEdit(null)} title={`Сотрудник: ${edit.name}`}
          footer={<><Button onClick={() => void run(() => http.patch(`/staff/${edit.id}`, { name: edit.name, role: edit.role, commissionPercent: edit.commissionPercent ?? 0 }), "Сохранено").then((ok) => ok && setEdit(null))}>Сохранить</Button><Button variant="outline" onClick={() => setEdit(null)}>Отмена</Button></>}>
          <FormGrid cols={2}>
            <Field label="Имя" required><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Роль" hint="После смены роли сотрудник войдёт заново"><Select value={edit.role} onValueChange={(v) => setEdit({ ...edit, role: v })} options={PLATFORM_ROLES.map((r) => ({ value: r, label: ROLES[r]! }))} /></Field>
            <Field label="Вознаграждение, % от оплат клиентов" hint="Считается от оплат клиентов, за которыми он закреплён менеджером. Отчёт — «Финансы сотрудников»">
              <NumberInput value={edit.commissionPercent} onValueChange={(v) => setEdit({ ...edit, commissionPercent: v })} min={0} max={100} precision={2} />
            </Field>
          </FormGrid>
        </Modal>
      )}
      {open && (
        <Modal open onOpenChange={(o) => !o && setOpen(false)} title="Новый сотрудник"
          footer={<><Button onClick={() => void run(() => http.post("/staff", f), "Сотрудник добавлен").then((ok) => ok && setOpen(false))}>Добавить</Button><Button variant="outline" onClick={() => setOpen(false)}>Отмена</Button></>}>
          <FormGrid cols={2}>
            <Field label="Имя" required><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Email" required><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
            <Field label="Роль"><Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })} options={PLATFORM_ROLES.map((r) => ({ value: r, label: ROLES[r]! }))} /></Field>
            <Field label="Временный пароль" required hint="Минимум 10 символов. Сотрудник сменит его сам"><PasswordInput value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
          </FormGrid>
        </Modal>
      )}
    </>
  );
}
